import { Model } from '../../../../types/model'
import { OverlayComponentProps } from '../../types'
import {
  AlertError,
  colors,
  fontSizes,
  InputTextArea,
  lineHeight,
  margins,
  PrimaryAction,
  Text,
} from '@ohgoodlord/design-system'
import { DefaultHeader, HEADING_ID } from '../default-header'
import { DefaultFooter } from '../default-footer'
import { css } from '@emotion/react'
import { Controller, useForm } from 'react-hook-form'
import { additionalDetailsValidationRules } from './validation'
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons'

export const ADDITIONAL_DETAILS = 'additional-details' as const

export interface AdditionalDetailsOverlayModel extends Model<typeof ADDITIONAL_DETAILS> {
  overlayTitle: string
  message?: string
  additionalDetails?: string
}

export interface FormValues {
  additionalDetails: string
}

export type Props = OverlayComponentProps<AdditionalDetailsOverlayModel>

export const AdditionalDetailsOverlay = ({
  model,
  isMobile,
  renderOverlayHeader,
  onOverlaySubmit,
  onSendEvent,
  onSendMessage,
  onClose,
}: Props) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    reValidateMode: 'onChange',
    defaultValues: {
      additionalDetails: model.additionalDetails ?? '',
    },
  })

  const onSubmit = ({ additionalDetails }: FormValues) => {
    onSendMessage({
      message: additionalDetails,
    })
    onSendEvent({
      name: 'form',
      value: { additionalDetails },
    })
    onOverlaySubmit()
  }

  return (
    <form
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
      `}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div>
        {renderOverlayHeader !== undefined ? (
          renderOverlayHeader()
        ) : (
          <DefaultHeader
            modal={model.modal}
            isMobile={isMobile}
            title={model.overlayTitle}
            onClose={onClose}
          />
        )}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 15px;
            padding: 15px;
          `}
        >
          <span
            css={css`
              line-height: ${lineHeight};
              color: ${colors.textSecondary};
              font-size: ${fontSizes.s};
            `}
            id={`${ADDITIONAL_DETAILS}-description`}
          >
            This will help us to process your reference.
          </span>
          {Controller({
            name: 'additionalDetails',
            control,
            rules: additionalDetailsValidationRules,
            render: ({ field, fieldState }) => (
              <InputTextArea
                {...field}
                style={{ height: '200px' }}
                aria-required="true"
                isInvalid={!!fieldState.error?.message}
                aria-invalid={!!fieldState.error?.message}
                id={ADDITIONAL_DETAILS}
                name={ADDITIONAL_DETAILS}
                aria-labelledby={HEADING_ID}
                aria-describedby={
                  fieldState.error
                    ? `${ADDITIONAL_DETAILS}-error ${ADDITIONAL_DETAILS}-description`
                    : `${ADDITIONAL_DETAILS}-description`
                }
              />
            ),
          })}
          {errors.additionalDetails && (
            <div
              id={`${ADDITIONAL_DETAILS}-error`}
              css={css`
                margin-top: ${margins.s};
              `}
            >
              <AlertError>
                <Text>{errors.additionalDetails.message}</Text>
              </AlertError>
            </div>
          )}
        </div>
      </div>
      <DefaultFooter
        renderActions={() => (
          <PrimaryAction type="submit" isFullWidth icon={faPaperPlane as any}>
            Send reason
          </PrimaryAction>
        )}
      />
    </form>
  )
}
